import { GridComponent } from '@syncfusion/ej2-react-grids';
import { RefObject } from 'react';
import { createFilterItem, IFilterItem } from '../data-types/filterItem';
import {
    IWhitePaperColumn,
    IWhitePaperRow,
    Report,
    ReportListCaseFilterOption,
    ReportListEntityFilterOption,
    ReportListRequest,
    ReturnFolderDto,
    Statement,
    TaxReturnTOCItem,
} from '../model';

export const updateFilterItems = (
    items: Report[],
    filterItem: string,
    setFilterItem: (items: IFilterItem[]) => void,
    createFilterItem: (itemValue: string) => IFilterItem
) => {
    const filterItemKey = filterItem as keyof Report;
    const uniqueItems = Array.from(new Set(items.map((item) => item[filterItemKey] ?? '')));
    setFilterItem(uniqueItems.map((item) => createFilterItem(item.toString())));
};

export const isRowIncludedInStatement = (row: IWhitePaperRow, statementBeginRow: number, statementEndRow: number) => {
    return row.rowNumber !== undefined && row.rowNumber >= statementBeginRow && row.rowNumber <= statementEndRow;
};

export const findFirstVisibleRow = (rows: IWhitePaperRow[], statement: Statement): IWhitePaperRow | undefined => {
    if (statement.beginRow === undefined || statement.endRow === undefined) {
        return undefined;
    }

    let leftLimit = 0;
    let rightLimit = rows.length - 1;
    let middle = Math.floor((leftLimit + rightLimit) / 2);
    let firstVisibleRow: IWhitePaperRow | undefined = undefined;

    while (leftLimit <= rightLimit && (firstVisibleRow === undefined || firstVisibleRow.rowNumber !== statement.beginRow)) {
        const currentRow = rows[middle];

        if (isRowIncludedInStatement(currentRow, statement.beginRow, statement.endRow)) {
            firstVisibleRow = rows[middle];
            rightLimit = middle - 1;
        } else {
            if (currentRow.rowNumber && currentRow.rowNumber < statement.beginRow) {
                leftLimit = middle + 1;
            } else if (currentRow.rowNumber && currentRow.rowNumber > statement.endRow) {
                rightLimit = middle - 1;
            }
        }

        middle = Math.floor((leftLimit + rightLimit) / 2);
    }

    return firstVisibleRow;
};

const cleanRowStatement = (key: Number, description: string): IFilterItem => {
    const clearDescription = description?.replace(/\*+/g, '').trim() || '';

    return {
        key: key.toString(),
        label: clearDescription,
        searchBy: [clearDescription],
    };
};

export const getSectionsFilterItem = (rows: IWhitePaperRow[], statements: TaxReturnTOCItem[]): IFilterItem[] => {
    const result: IFilterItem[] = [];

    const tempStatements: TaxReturnTOCItem[] = [...statements];
    tempStatements.sort((a, b) => (a.beginRow || 0) - (b.beginRow || 0));
    for (const row of rows) {
        if (
            row.rowNumber !== undefined &&
            tempStatements[0] &&
            row.rowNumber >= (tempStatements[0].beginRow || 0) &&
            row.rowNumber <= (tempStatements[0].endRow || 0)
        ) {
            const statement = tempStatements.shift();
            result.push(cleanRowStatement(row.rowNumber!, statement?.description!));
        } else {
            if (row.isAnchor) {
                result.push(cleanRowStatement(row.rowNumber!, row.description!));
            }
        }
    }

    return result;
};

export const getLegacyEntitiesFilterItem = (items: ReportListEntityFilterOption[]) => {
    const entities: IFilterItem[] = [];

    items.forEach((item) => {
        if (!entities.some((e) => e.key === item.entityCode)) {
            entities.push({
                key: `${item.entityCode}`,
                label: `${item.displayName}`,
                searchBy: [item.displayName ?? ''],
            });
        }
    });

    return entities;
};

export const getLegacyCasesFilterItem = (availableFilterItems: ReportListCaseFilterOption[]) => {
    const casesFilterItems: IFilterItem[] = [];

    availableFilterItems.forEach((item) => {
        if (!casesFilterItems.some((e) => e.key === item.caseCode)) {
            casesFilterItems.push({
                key: `${item.caseCode}`,
                label: `${item.displayName}`,
                searchBy: [item.displayName ?? ''],
            });
        }
    });

    return casesFilterItems;
};

export const getLegacyFilterOptions = (availableFilterOptions: string[] | null | undefined): IFilterItem[] => {
    return availableFilterOptions?.map((option) => createFilterItem(option)) ?? [];
};

export const getEntitiesFilterForColumns = (columns: IWhitePaperColumn[]) => {
    const entities: IFilterItem[] = [];

    columns.forEach((col) => {
        if (!entities.some((e) => e.key === col.fieldName)) {
            entities.push({
                key: `${col.fieldName}`,
                label: `${col.entityCode} - ${col.entityName}`,
                searchBy: [col.entityCode ?? '', col.entityName ?? ''],
            });
        }
    });

    return entities;
};

export const performFolderFiltering = (filters: IFilterItem[], folderItems: ReturnFolderDto[] | undefined, result: Report[]): Report[] => {
    const selectedFolders = new Set(filters.map((filter) => filter.key));
    const reportsForFolderItems = new Set(
        folderItems
            ?.filter((item) => {
                return selectedFolders.has(item.folderName ?? '');
            })
            ?.flatMap((item) => item.returnKeys)
    );
    result = result.filter((report) => {
        return reportsForFolderItems.has(report.id);
    });
    return result;
};

export const reorderCheckedItems = (checkedItems: IFilterItem[], allItems: IFilterItem[], filterName: string) => {
    const checkedSet = new Set(checkedItems.map((item) => item.key));
    const customSort = (a: IFilterItem, b: IFilterItem) => {
        if (filterName === 'case') {
            return parseInt(a.label, 10) - parseInt(b.label, 10);
        }

        return a.label.localeCompare(b.label);
    };

    const checkedSortedItems = [...checkedItems].sort(customSort);
    const uncheckedSortedItems = allItems.filter((item) => !checkedSet.has(item.key)).sort(customSort);

    return [...checkedSortedItems, ...uncheckedSortedItems];
};

export const getUpdatedSelectedFilters = (
    selectedItem: IFilterItem[],
    currentlySelectedItems: IFilterItem[],
    isSingleSelect: boolean = false
) => {
    let newSelectedItems: IFilterItem[] = [];

    if (selectedItem.length > 0) {
        if (isSingleSelect && currentlySelectedItems.some((x) => x.key === selectedItem[0].key)) {
            newSelectedItems = [];
        } else {
            newSelectedItems = [...selectedItem];
        }
    }

    return newSelectedItems;
};

/**
 * Grid-side Excel filters and toolbar search are client-side and can target fields that have no equivalent
 * `ReportListRequest` filter. Preserve the exact result set by sending its report IDs to the API.
 */
const getActiveGridColumnFilters = (grid: GridComponent): Partial<ReportListRequest> => {
    const hasColumnFilter = (grid.filterSettings?.columns?.length ?? 0) > 0;
    const hasToolbarSearch = !!grid.searchSettings?.key?.trim();
    if (!hasColumnFilter && !hasToolbarSearch) {
        return {};
    }

    const filteredRecords = (grid.getFilteredRecords?.() as Report[]) ?? [];
    return {
        selectedIds: Array.isArray(filteredRecords)
            ? filteredRecords.map((report) => report.id).filter((id): id is number => id !== null && id !== undefined)
            : [],
    };
};

/**
 * Builds the export/cart payload from caller-owned selected records.
 * Callers must resolve selection themselves (e.g. from a tracked id set): Syncfusion's
 * getSelectedRecords() is unreliable with checkbox selection + virtualization once rows
 * scroll out of the viewport.
 * Returns the full selection; callers decide eligibility (e.g. details export drops canExecute=false rows).
 */
export const getSelectedTaxReturnsAndFilters = (
    gridComponentRef: RefObject<GridComponent>,
    getTaxReturnsRequestFunc: { (): ReportListRequest; (): ReportListRequest },
    selectedRecords: Report[]
) => {
    if (gridComponentRef?.current) {
        const selectedTaxReturns = selectedRecords;
        const filters = { ...getTaxReturnsRequestFunc(), ...getActiveGridColumnFilters(gridComponentRef.current) };
        return { selectedTaxReturns, filters };
    }
    return {};
};

export const getFilterMenuAriaLabel = (labelName: string, t: (value: string) => string) => {
    return `${t(labelName)} ${t('filter')}`;
};

export const getFilterItemDisplayLabel = (item: IFilterItem): string => {
    const label = item.label ? item.label.trim() : '';
    return label === '' ? '(Blanks)' : label;
};

export const getFilteredFilterItems = (items: IFilterItem[], searchValue?: string): IFilterItem[] => {
    return items.filter((item) => {
        const filterItemSearchBy: string[] = item?.searchBy as string[];
        return filterItemSearchBy?.some((x) => x?.toLowerCase().includes(searchValue?.toLocaleLowerCase() ?? ''));
    });
};
